import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect } from 'react';

import { INPUT_TYPES } from '../../../constants';
import { removePlusSignSuffix } from '../../../utils/removePlusSignSuffix';
import { removeHyphensAndCapitalize } from '../../../utils/removeHyphensAndCapitalize';
import { SectionOwnerForm } from '../../../pages/bcp/strategies/components';
import { message } from 'antd';
import { cleanUpObject } from '../../../utils';
import BaseForm from './BaseForm';
import { AutomatedFormProps } from './types';

type actionType = 'create' | 'update';

type FormProps = AutomatedFormProps & {
  createGQL?: any;
  updateGQL?: any;
  deleteGQL?: any;
  modifyVariables?: (
    data: any,
    type?: actionType,
    currentRecord?: any,
  ) => object;
  onFinalize?: () => void;
  canDelete?: boolean;
  setIsLoading?: (loading: boolean) => void;
  forceSubmit?: boolean;
  forceSubmitRecord?: any;
  isSectionOwnerForm?: boolean;
};

const StandaloneForm = ({
  fields,
  editFields,
  currentRecord,
  createGQL,
  updateGQL,
  deleteGQL,
  modifyVariables,
  onFinalize,
  canDelete = true,
  setIsLoading,
  forceSubmit,
  forceSubmitRecord,
  isSectionOwnerForm,
}: FormProps) => {
  const fieldsToUse = currentRecord && editFields ? editFields : fields;

  const formInstance = useForm();

  const { reset } = formInstance;

  const onSubmit: SubmitHandler<any> = async (values: any) => {
    const mutation = currentRecord ? updateGQL : createGQL;
    const type = currentRecord ? 'update' : 'create';

    let data;

    try {
      data = modifyVariables
        ? modifyVariables({ values, type, currentRecord })
        : values;
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Operation Failed',
      );
      return;
    }

    const cleanedData = cleanUpObject(data);

    setIsLoading && setIsLoading(true);
    try {
      if (!currentRecord) {
        await mutation({
          variables: {
            data: {
              ...cleanedData,
            },
          },
        });
      } else {
        await mutation({
          variables: {
            data: { ...cleanedData, uuid: currentRecord?.uuid },
          },
        });
      }
      message.open({
        type: 'success',
        content: 'Changes Saved',
      });
    } catch (error) {
      message.open({
        type: 'error',
        content: error instanceof Error ? error.message : 'Operation Failed',
      });
      console.log(error instanceof Error ? error.message : 'Operation Failed');
    } finally {
      onFinalize && onFinalize();
      setIsLoading && setIsLoading(false);
    }
  };

  const onDelete: any = async () => {
    setIsLoading && setIsLoading(true);
    try {
      await deleteGQL({
        variables: {
          uuid: currentRecord?.uuid,
        },
      });
      message.open({
        type: 'success',
        content: 'Entry Deleted',
      });
    } catch (error) {
      message.open({
        type: 'error',
        content: 'Operation Failed',
      });
    } finally {
      onFinalize && onFinalize();
      setIsLoading && setIsLoading(false);
    }
  };

  useEffect(() => {
    const defaultValues: { [key: string]: string } = fieldsToUse?.reduce(
      (acc, field) => {
        if (!field.key) return acc;
        if (currentRecord) {
          acc[field.key] = currentRecord[field.key];

          if (field.type === INPUT_TYPES.SELECT) {
            acc[field.key] = currentRecord[field.key] ?? '';

            if (acc[field.key] !== '')
              acc[field.key] = removeHyphensAndCapitalize(
                removePlusSignSuffix(currentRecord[field.key]),
              );
          }

          if (field.type === INPUT_TYPES.DATE) {
            acc[field.key] = new Date(currentRecord[field.key])
              .toISOString()
              .split('T')[0];
          }
        } else {
          acc[field.key] = '';
        }
        return acc;
      },
      {} as { [key: string]: string },
    );

    reset((prev) => ({ ...prev, ...defaultValues }));
  }, [currentRecord, onFinalize]);

  useEffect(() => {
    if (forceSubmit && forceSubmitRecord) {
      onSubmit(forceSubmitRecord);
    }
  }, [forceSubmit, forceSubmitRecord]);

  if (isSectionOwnerForm) {
    return <SectionOwnerForm data={currentRecord} onFinish={onSubmit} />;
  }

  return (
    <BaseForm
      formInstance={formInstance}
      fields={fieldsToUse}
      currentRecord={currentRecord}
      onSubmit={onSubmit}
      canDelete={canDelete}
      onDelete={onDelete}
      resetForm={reset}
    />
  );
};

export default StandaloneForm;
